import { Component, OnInit } from '@angular/core';
import { MenuItem, MessageService } from "primeng/api";
import { ILeaseResumed } from "@modules/private/lease/data/interfaces/ilease.interface";
import { LeaseDetailService } from '../../data/services/view/lease-detail.service';
import { PdfQuotationApiService } from '@data/services/utils/pdf-quotation-api.service';
import { ApiQuoteParametersService } from '@modules/private/quote/data/services/api/api-quote-parameters.service';
import { QuoteParametersService } from '@modules/private/quote/data/services/view/quote-parameters.service';
import { monthlyRentBreakdown } from '@modules/private/quote/data/utils/monthly-rent.util';

@Component({
  selector: 'app-lease-quotes',
  templateUrl: './lease-quotes.component.html',
  styleUrls: ['./lease-quotes.component.scss'],
  providers: [MessageService]
})
export class LeaseQuotesComponent implements OnInit {
  items: MenuItem[] | undefined;
  lease = this.data._id
  selectedTermId?: string
  selectedQuoteId?: string
  quote = {}
  loading = false;
  calculatedQuote: any = null;

  // Cotización calculada al vuelo desde los parámetros (vista "Pendiente de Activación").
  // Trae selectedTerms[] (uno por plazo) + montos top-level (assetsValue, advance, etc.).
  calculatedParams: any = null;
  selectedParamTermIndex = 0;
  // Opciones del selector de plazo, estables (no se calculan en el template).
  paramTermOptions: { label: string; value: number }[] = [];

  // Variables para modal de edición de parameters
  showEditModal = false;
  editingParameters = false;

  // Variable para modal de opciones PDF
  showPdfOptionsDialog = false;

  readonly rentBreakdown = monthlyRentBreakdown;

  ngOnInit() {
    this.lease = this.data._id
    this.loadQuoteData();
    this.items = [
      {
        label: 'Tabla de amortización',
        icon: 'fa-solid fa-table-cells',
        command: (event: any) => {
          this.showAmortizationTable()
        }
      },
      {
        label: 'Descarga PDF',
        icon: 'fa-solid fa-file-pdf',
        command: (event: any) => {
          this.downloadPDF()
        }
      },
      {
        label: 'Seleccionar cotización',
        icon: 'fa-solid fa-circle-check',
        command: (event: any) => {
          this.selectQuote()
        }
      }
    ]
  }

  constructor(
    private leaseDetailService: LeaseDetailService,
    private pdfQuotationService: PdfQuotationApiService,
    public messageService: MessageService,
    private apiQuoteParametersService: ApiQuoteParametersService,
    public quoteParametersService: QuoteParametersService
  ) { }

  get data() {
    return this.leaseDetailService.data as any
  }

  /**
   * Cargar datos de la cotización
   */
  loadQuoteData() {
    if (this.hasCompleteQuoteData()) {
      this.calculatedQuote = this.data.quotation;
    } else if (this.hasQuotationParameters() && this.data.quotationParameters._id && !this.calculatedParams) {
      // Vista "Pendiente de Activación": calcular al vuelo para mostrar los desgloses por
      // plazo. Es un cómputo read-only (no persiste); el endpoint está gateado con get-by-id.
      this.apiQuoteParametersService.calculateQuote(this.data.quotationParameters._id).subscribe((r) => {
        if (!r.error) {
          this.calculatedParams = r.data;
          this.paramTermOptions = (this.calculatedParams?.selectedTerms ?? []).map((t: any, i: number) => ({
            label: t.term + ' meses',
            value: i
          }));
          this.selectedParamTermIndex = 0;
        }
      });
    }
  }

  /**
   * Plazo seleccionado en el selector de la vista de parámetros (cotización calculada al vuelo).
   */
  get selectedParamTerm() {
    return this.calculatedParams?.selectedTerms?.[this.selectedParamTermIndex] ?? null;
  }

  /**
   * Valor Cartera (rentas × plazo) derivado del término — no viene del backend.
   */
  getCartera(term: any) {
    if (!term?.rent) return { subtotal: 0, iva: 0, total: 0 };
    return {
      subtotal: (term.rent.amount + term.rent.accessories) * term.term,
      iva: term.rent.iva * term.term,
      total: term.rent.total * term.term
    };
  }

  /**
   * Verificar si hay quotationParameters disponibles
   */
  hasQuotationParameters(): boolean {
    return !!this.data?.quotationParameters;
  }

  /**
   * Verificar si la cotización tiene estructura completa con termData
   */
  hasCompleteQuoteData(): boolean {
    return this.data?.quotation?.termData && this.data.quotation.amortizationPeriods;
  }

  /**
   * Obtener datos de quotationParameters
   */
  getQuotationParametersData() {
    return this.data?.quotationParameters || null;
  }

  /**
   * Obtener términos de quotationParameters
   */
  getQuotationParametersTerms() {
    if (this.data?.quotationParameters?.terms) {
      return this.data.quotationParameters.terms;
    }
    return [];
  }

  /**
   * Obtener datos de término aprobado (compatible con nueva estructura)
   */
  getApprovedTermData() {
    if (this.data?.quotation?.termData) {
      // Nueva estructura con termData
      return this.data.quotation.termData;
    } else if (this.data?.quotation?.approvedTerm) {
      // Estructura legacy
      return this.data.quotation.approvedTerm;
    }
    return null;
  }

  /**
   * Obtener términos seleccionados (compatible con nueva estructura)
   */
  getSelectedTermsData() {
    if (this.data?.quotation?.termData) {
      // Si hay termData, crear array con ese término para compatibilidad
      return [this.data.quotation.termData];
    } else if (this.data?.quotation?.selectedTerms) {
      // Estructura legacy
      return this.data.quotation.selectedTerms;
    }
    return [];
  }

  /**
   * Formatear montos para visualización
   */
  formatCurrency(amount: number): string {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN'
    }).format(amount);
  }

  showErrorMessage(message: string) {
    this.messageService.add({ severity: 'error', summary: 'Error', detail: message });
  }

  showSuccessMessage(message: string) {
    this.messageService.add({ severity: 'success', summary: 'Éxito', detail: message });
  }

  onMenuClick(termId: string, quote: any, event: MouseEvent, menu: any) {
    this.selectedTermId = termId;
    this.selectedQuoteId = quote._id || quote.term;
    this.quote = quote;

    menu.toggle(event);
  }

  updateQuote() { 
    // Método reservado para funcionalidad futura
  }

  /**
   * Mostrar tabla de amortización
   */
  showAmortizationTable() {
    console.log('Tabla de amortización - Term ID:', this.selectedTermId, 'Quote ID:', this.selectedQuoteId);
    
    if (this.hasCompleteQuoteData()) {
      console.log('Cotización con estructura completa detectada');
      console.log('AmortizationPeriods disponibles:', this.data.quotation.amortizationPeriods?.length || 0);
    } else {
      console.log('Usando quotationParameters');
    }
  }

  /**
   * Descargar PDF - Muestra confirmación para incluir/omitir residual antes de generar
   */
  downloadPDF() {
    this.confirmResidualAndGeneratePdf();
  }

  /**
   * Diálogo de confirmación para incluir/omitir valor residual en el PDF
   */
  private confirmResidualAndGeneratePdf() {
    this.showPdfOptionsDialog = true;
  }

  /**
   * Cerrar el diálogo de opciones PDF sin generar nada
   */
  closePdfDialog() {
    this.showPdfOptionsDialog = false;
  }

  /**
   * Generar PDF con la opción de residual seleccionada
   */
  generatePdf(includeResidual: boolean) {
    this.showPdfOptionsDialog = false;
    this.executePdfGeneration(includeResidual);
  }

  /**
   * Ejecutar la generación del PDF con la opción de residual seleccionada
   */
  private async executePdfGeneration(includeResidual: boolean) {
    const pdfOptions = { includeResidual };

    // Si ya hay cotización calculada, descargar directamente
    if (this.hasCompleteQuoteData()) {
      const quotation = { ...this.data.quotation };
      const hasValidClient = quotation.client && typeof quotation.client === 'object' && quotation.client.name;
      if (!hasValidClient && this.data.client) {
        quotation.client = this.data.client;
      }
      if ((!quotation.assets || quotation.assets.length === 0) && this.data.assets) {
        quotation.assets = this.data.assets;
      }
      try {
        // PDF centralizado (ADR-0005): Quotation guardada → el backend carga y popula client/assets.
        await this.pdfQuotationService.generateQuotationPdfById(this.data.quotation._id, pdfOptions);
        this.showSuccessMessage('El PDF ha sido descargado correctamente.');
      } catch (error) {
        console.error('Error generating PDF:', error);
        this.showErrorMessage('Error al generar el PDF de la cotización.');
      }
    } 
    // Si solo hay quotationParameters, primero calcular la cotización
    else if (this.hasQuotationParameters()) {
      this.messageService.add({ 
        severity: 'info', 
        summary: 'Procesando', 
        detail: 'Calculando cotización para generar PDF...' 
      });

      this.apiQuoteParametersService.calculateQuote(this.data.quotationParameters._id).subscribe({
        next: async (response) => {
          if (!response.error && response.data) {
            try {
              const enrichedData = { ...response.data };
              const hasValidClient = enrichedData.client && typeof enrichedData.client === 'object' && enrichedData.client.name;
              if (!hasValidClient && this.data.client) {
                enrichedData.client = this.data.client;
              }
              if ((!enrichedData.assets || enrichedData.assets.length === 0) && this.data.assets) {
                enrichedData.assets = this.data.assets;
              }
              // PDF centralizado (ADR-0005): QuotationParameters → el backend recalcula con createQuote.
              await this.pdfQuotationService.generateQuotationPdf(this.data.quotationParameters._id, pdfOptions);
              this.showSuccessMessage('El PDF se ha descargado correctamente.');
            } catch (error) {
              console.error('Error generating PDF:', error);
              this.showErrorMessage('Error al generar el PDF de la cotización.');
            }
          } else {
            this.showErrorMessage(response.message || 'Error al calcular la cotización para PDF.');
          }
        },
        error: (error) => {
          console.error('Error calculating quotation for PDF:', error);
          this.showErrorMessage('Error al calcular la cotización para PDF.');
        }
      });
    } else {
      this.showErrorMessage('No hay datos de cotización disponibles para generar PDF.');
    }
  }

  /**
   * Seleccionar cotización (mantenido sin cambios)
   */
  async selectQuote() {
    if (this.selectedQuoteId && this.selectedTermId) {
      try {
        await this.leaseDetailService.approveLeaseTerm(this.lease, this.selectedQuoteId, this.selectedTermId);
        this.showSuccessMessage('El término de la cotización ha sido actualizado correctamente.');
      } catch (error) {
        this.showErrorMessage('Ocurrió un error al actualizar el término de la cotización.');
        console.error('Error al aprobar término:', error);
      }
    } else {
      this.showErrorMessage('Seleccione una cotización y término válidos.');
    }
  }

  // ===== MÉTODOS HELPER PARA VISUALIZACIÓN =====

  /**
   * Verificar si hay información de cliente
   */
  hasClientInfo(): boolean {
    return !!(this.calculatedQuote?.client || this.data?.client);
  }

  /**
   * Obtener nombre del cliente
   */
  getClientName(): string {
    if (this.calculatedQuote?.client?.name) {
      return this.calculatedQuote.client.name;
    }
    if (this.data?.client?.name) {
      return this.data.client.name;
    }
    return 'Cliente no especificado';
  }

  /**
   * Verificar si hay activos definitivos
   */
  hasDefinitiveAssets(): boolean {
    const assets = this.calculatedQuote?.assets || this.data?.assets || this.data?.asset;
    return !!(assets && (Array.isArray(assets) ? assets.length > 0 : true));
  }

  /**
   * Obtener el número total de activos
   */
  getTotalAssetsCount(): number {
    if (this.hasDefinitiveAssets()) {
      const assets = this.calculatedQuote?.assets || this.data?.assets;
      if (Array.isArray(assets)) {
        return assets.length;
      }
      return 1;
    }
    return this.calculatedQuote?.assetsNames ? 1 : 0;
  }

  /**
   * Obtener lista de activos definitivos
   */
  getDefinitiveAssets(): any[] {
    const assets = this.calculatedQuote?.assets || this.data?.assets;
    if (assets && Array.isArray(assets) && assets.length > 0) {
      return assets;
    }
    // Si hay un solo activo (no array)
    const singleAsset = this.data?.asset;
    if (singleAsset) {
      return [singleAsset];
    }
    return [];
  }

  /**
   * Verificar si los activos tienen información detallada
   */
  hasDetailedAssets(): boolean {
    const assets = this.getDefinitiveAssets();
    return assets.length > 0 && assets[0]?.name && assets[0]?.category;
  }

  /**
   * Obtener resumen de categorías de activos
   */
  getAssetsCategorySummary(): string {
    const assets = this.getDefinitiveAssets();
    if (assets.length === 0) return '';
    
    const categories = assets.map((asset: any) => asset.category?.name || asset.category).filter(Boolean);
    const uniqueCategories = [...new Set(categories)];
    
    if (uniqueCategories.length === 1) {
      return uniqueCategories[0] as string;
    } else if (uniqueCategories.length > 1) {
      return `${uniqueCategories.length} categorías diferentes`;
    }
    return '';
  }

  /**
   * Obtener tabla de amortización procesada
   */
  getAmortizationPeriods(): any[] {
    if (!this.calculatedQuote?.amortizationPeriods) return [];
    
    // Si es array de objetos completos, devolverlos
    if (Array.isArray(this.calculatedQuote.amortizationPeriods) && 
        this.calculatedQuote.amortizationPeriods.length > 0 &&
        typeof this.calculatedQuote.amortizationPeriods[0] === 'object') {
      return this.calculatedQuote.amortizationPeriods;
    }
    
    return [];
  }

  /**
   * Obtener detalles de accesorios formateados
   */
  getAccessoriesDetails() {
    if (!this.calculatedQuote?.termData?.accessoriesDetail) return null;
    
    return {
      cash: this.calculatedQuote.termData.accessoriesDetail.cash,
      financed: this.calculatedQuote.termData.accessoriesDetail.financed
    };
  }

  /**
   * Verificar si hay accesorios configurados (con datos reales)
   */
  hasAccessories(): boolean {
    const details = this.getAccessoriesDetails();
    if (!details) return false;
    
    const hasCash = details.cash && details.cash.amount > 0;
    const hasFinanced = details.financed && details.financed.total && details.financed.total.amount > 0;
    
    return hasCash || hasFinanced;
  }

  /**
   * Función de tracking para optimizar el renderizado de activos
   */
  trackByAssetId(index: number, asset: any): string {
    return asset._id || asset.id || index.toString();
  }

  /**
   * Obtener clase CSS para el estado del activo
   */
  getAssetStatusClass(status: string): string {
    switch (status?.toLowerCase()) {
      case 'disponible':
        return 'status-available';
      case 'arrendado':
        return 'status-leased';
      case 'mantenimiento':
        return 'status-maintenance';
      default:
        return 'status-unknown';
    }
  }

  /**
   * Formatear fecha
   */
  formatDate(date: Date | string): string {
    if (!date) return 'No disponible';
    return new Date(date).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  }

  /**
   * Verificar si un valor existe y no está vacío
   */
  hasValue(value: any): boolean {
    return value !== null && value !== undefined && value !== '';
  }

  // ===== MÉTODOS PARA VISTA DE QUOTATION PARAMETERS =====

  /**
   * Verificar si se pueden editar los parameters (solo si NO hay quotation definitiva)
   */
  canEditParameters(): boolean {
    return this.hasQuotationParameters() && !this.hasCompleteQuoteData();
  }

  /**
   * Obtener nombre del cliente desde parameters
   */
  getParametersClientName(): string {
    const params = this.data?.quotationParameters;
    if (params?.client?.name) {
      return params.client.name;
    }
    if (params?.provitionalClient) {
      return params.provitionalClient;
    }
    return 'Cliente no especificado';
  }

  /**
   * Verificar si parameters tiene cliente definitivo
   */
  hasParametersDefinitiveClient(): boolean {
    return !!(this.data?.quotationParameters?.client?.name);
  }

  /**
   * Verificar si parameters tiene activos definitivos
   */
  hasParametersDefinitiveAssets(): boolean {
    const params = this.data?.quotationParameters;
    return !!(params?.assets && params.assets.length > 0);
  }

  /**
   * Obtener total de activos desde parameters
   */
  getParametersTotalAssetsCount(): number {
    if (this.hasParametersDefinitiveAssets()) {
      return this.data.quotationParameters.assets.length;
    }
    return this.data?.quotationParameters?.provitionalAsset ? 1 : 0;
  }

  /**
   * Obtener activos definitivos desde parameters
   */
  getParametersDefinitiveAssets(): any[] {
    const params = this.data?.quotationParameters;
    if (params?.assets && Array.isArray(params.assets) && params.assets.length > 0) {
      return params.assets;
    }
    return [];
  }

  /**
   * Verificar si parameters tiene activos con información detallada
   */
  hasParametersDetailedAssets(): boolean {
    const assets = this.getParametersDefinitiveAssets();
    return assets.length > 0 && assets[0]?.name && assets[0]?.category;
  }

  /**
   * Obtener resumen de categorías desde parameters
   */
  getParametersAssetsCategorySummary(): string {
    const assets = this.getParametersDefinitiveAssets();
    if (assets.length === 0) return '';
    
    const categories = assets.map((asset: any) => asset.category?.name || asset.category).filter(Boolean);
    const uniqueCategories = [...new Set(categories)];
    
    if (uniqueCategories.length === 1) {
      return uniqueCategories[0] as string;
    } else if (uniqueCategories.length > 1) {
      return `${uniqueCategories.length} categorías diferentes`;
    }
    return '';
  }

  /**
   * Obtener total de accesorios desde parameters
   */
  getParametersAccessoriesCount(): number {
    const params = this.data?.quotationParameters;
    if (params?.accessories && Array.isArray(params.accessories)) {
      return params.accessories.length;
    }
    return 0;
  }

  /**
   * Obtener accesorios regulares desde parameters
   */
  getParametersRegularAccessories(): any[] {
    const params = this.data?.quotationParameters;
    if (params?.accessories && Array.isArray(params.accessories)) {
      return params.accessories.filter((acc: any) => acc.type !== 'insurance');
    }
    return [];
  }

  /**
   * Obtener accesorios de seguro desde parameters
   */
  getParametersInsuranceAccessories(): any[] {
    const params = this.data?.quotationParameters;
    if (params?.accessories && Array.isArray(params.accessories)) {
      return params.accessories.filter((acc: any) => acc.type === 'insurance');
    }
    return [];
  }

  // ===== MÉTODOS PARA MODAL DE EDICIÓN =====

  /**
   * Abrir modal de edición de parameters
   */
  openEditModal() {
    if (this.canEditParameters()) {
      // Configurar el servicio en modo EDIT
      this.quoteParametersService.setFormMethod('EDIT');
      this.quoteParametersService.quote = this.data.quotationParameters;
      
      // Inicializar el formulario con los datos existentes
      this.quoteParametersService.initializeForm();
      
      this.showEditModal = true;
    } else {
      this.showErrorMessage('No se pueden editar los parámetros: la cotización ya fue activada.');
    }
  }

  /**
   * Cancelar edición y cerrar modal
   */
  onFormCancel() {
    this.showEditModal = false;
    this.quoteParametersService.setFormMethod('CREATE');
    this.quoteParametersService.quote = null;
    this.quoteParametersService.entity.reset();
    this.quoteParametersService.termsArray.clear();
  }

  /**
   * Construye el payload normalizado que espera el backend, replicando la lógica
   * canónica del flujo de creación (quote-parameters-preview.component). Es la
   * fuente única para "Calcular" (preview) y "Guardar" (PUT) dentro de este modal.
   * - terms: objetos {term, residuary} desde termsArray (no los números crudos del multiselect).
   * - accessories: insuranceDetails = null cuando no es seguro; fechas a Date cuando aplica.
   * - provitionalValue: se omite cuando hay activos reales para que el backend use los activos.
   */
  private buildNormalizedPayload(): any {
    const data = { ...this.quoteParametersService.entity.value };
    data.user = this.data.quotationParameters.user?._id || this.data.quotationParameters.user;
    data.terms = this.quoteParametersService.termsArray.value;

    if (!this.quoteParametersService.useProvisionalData && data.assets?.length > 0) {
      delete data.provitionalValue;
    }

    if (data.accessories && data.accessories.length > 0) {
      data.accessories.forEach((accessory: any) => {
        if (accessory.insuranceDetails) {
          if (accessory.insuranceDetails.startPeriod !== null && accessory.insuranceDetails.endPeriod !== null) {
            accessory.insuranceDetails.startPeriod = new Date(accessory.insuranceDetails.startPeriod);
            accessory.insuranceDetails.endPeriod = new Date(accessory.insuranceDetails.endPeriod);
          }
        }
        if (accessory.isInsurance === false) {
          accessory.insuranceDetails = null;
        }
      });
    } else {
      data.accessories = [];
    }

    return data;
  }

  /**
   * Guardar cambios en parameters
   */
  saveForm() {
    if (!this.quoteParametersService.entity.valid) {
      this.showErrorMessage('Por favor complete todos los campos requeridos.');
      return;
    }

    this.editingParameters = true;
    const formData = this.buildNormalizedPayload();

    this.apiQuoteParametersService.update(this.data.quotationParameters._id, formData).subscribe({
      next: (response) => {
        if (!response.error) {
          this.showSuccessMessage('Parámetros actualizados correctamente.');
          this.showEditModal = false;
          // Recargar el lease para obtener datos actualizados
          this.leaseDetailService.getLeaseDetail();
          this.loadQuoteData();
        } else {
          this.showErrorMessage(response.message || 'Error al actualizar los parámetros.');
        }
        this.editingParameters = false;
      },
      error: (error) => {
        console.error('Error updating parameters:', error);
        this.showErrorMessage('Error al actualizar los parámetros.');
        this.editingParameters = false;
      }
    });
  }

  /**
   * Calcular cotización (previsualización) desde el modal de edición.
   * SOLO calcula los montos y los muestra en el panel del cotizador (quoteAmounts$);
   * NO persiste y NO cierra el modal. La persistencia ocurre únicamente en saveForm().
   */
  getAmounts() {
    if (!this.quoteParametersService.entity.valid) {
      this.showErrorMessage('Por favor complete todos los campos requeridos.');
      return;
    }

    this.editingParameters = true;
    const data = this.buildNormalizedPayload();
    delete data.case;
    // F-016: la bandera va solo en la vista previa, con el valor que la cotización ya tiene guardado.
    // buildNormalizedPayload() también arma el PUT de saveForm(), cuyo DTO la rechaza: por eso se añade aquí.
    data.advancedRentsIncludeAccessories = this.quoteParametersService.advancedRentsIncludeAccessoriesForPreview();

    this.apiQuoteParametersService.getAmounts(data).subscribe({
      next: (response) => {
        this.editingParameters = false;
        this.quoteParametersService.updateQuoteAmounts(response.data);
        this.messageService.add({
          severity: 'info',
          summary: 'Montos actualizados',
          detail: 'Los montos se calcularon correctamente'
        });
      },
      error: (error) => {
        console.error('Error calculating amounts:', error);
        this.editingParameters = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Error al calcular montos',
          detail: 'Por favor, verifica los datos ingresados'
        });
      }
    });
  }
}
